import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaHome } from 'react-icons/fa';
import axios from 'axios';
import { LOGIN_ENDPOINT, REGISTER_ENDPOINT } from '../api.js';
import styles from './Login.module.css';
import logoImage from '../assets/logo.jpg';

const Login = () => {
  // Admin login state
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminShowPassword, setAdminShowPassword] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminLoggedIn, setAdminLoggedIn] = useState(() => {
    return localStorage.getItem('loggedIn') === 'true';
  });

  // User card mode: 'signup' or 'login'
  const [userMode, setUserMode] = useState('signup');

  // User signup state
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userShowPassword, setUserShowPassword] = useState(false);
  const [userError, setUserError] = useState('');
  const [userSuccess, setUserSuccess] = useState('');

  // User login state
  const [userLoginEmail, setUserLoginEmail] = useState('');
  const [userLoginPassword, setUserLoginPassword] = useState('');
  const [userLoginShowPassword, setUserLoginShowPassword] = useState(false);
  const [userLoginError, setUserLoginError] = useState('');

  const navigate = useNavigate();

  // Check if user is already logged in and redirect accordingly
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('loggedIn') === 'true';
    const userRole = localStorage.getItem('userRole');

    if (isLoggedIn) {
      if (userRole === 'admin') {
        navigate('/admin/dashboard');
      } else if (userRole === 'user') {
        navigate('/user/dashboard');
      }
    }
  }, [navigate]);

  // Admin login handler
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminError('');
    try {
      const response = await axios.post(LOGIN_ENDPOINT, {
        email: adminUsername.toLowerCase().trim(),
        password: adminPassword,
        role: 'admin'
      });
      setAdminLoggedIn(true);
      localStorage.setItem('loggedIn', 'true');
      localStorage.setItem('userEmail', adminUsername);
      localStorage.setItem('userRole', 'admin');
      localStorage.setItem('userPassword', adminPassword);
      localStorage.setItem('userData', JSON.stringify(response.data.user));
      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Admin login error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Login failed. Please check your connection.';
      setAdminError(errorMessage);
    }
  };

  // User signup handler (front-end only)
  const handleUserSignup = async (e) => {
    e.preventDefault();
    setUserError('');
    setUserSuccess('');
    if (!userName || !userEmail || !userPassword) {
      setUserError('All fields are required');
      return;
    }
    try {
      const response = await axios.post(REGISTER_ENDPOINT, {
        username: userName.toLowerCase().trim(),
        email: userEmail.toLowerCase().trim(),
        password: userPassword,
        role: 'user'
      });
      setUserSuccess('Account created! Please log in.');
      setUserName('');
      setUserEmail('');
      setUserPassword('');
      setTimeout(() => {
        setUserMode('login');
        setUserSuccess('');
      }, 1200);
    } catch (err) {
      console.error('User signup error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Signup failed. Please check your connection.';
      setUserError(errorMessage);
    }
  };

  // User login handler (front-end only)
  const handleUserLogin = async (e) => {
    e.preventDefault();
    setUserLoginError('');
    if (!userLoginEmail || !userLoginPassword) {
      setUserLoginError('Both fields are required');
      return;
    }
    try {
      const response = await axios.post(LOGIN_ENDPOINT, {
        email: userLoginEmail.toLowerCase().trim(),
        password: userLoginPassword,
        role: 'user'
      });
      // If login is successful
      localStorage.setItem('loggedIn', 'true');
      localStorage.setItem('userEmail', userLoginEmail);
      localStorage.setItem('userRole', 'user');
      localStorage.setItem('userPassword', userLoginPassword);
      localStorage.setItem('userData', JSON.stringify(response.data.user));
      navigate('/user/dashboard');
    } catch (err) {
      console.error('User login error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Login failed. Please check your connection.';
      setUserLoginError(errorMessage);
    }
  };

  // Admin logout
  const handleAdminLogout = () => {
    console.log('Admin logout initiated');

    setAdminLoggedIn(false);

    // Clear all possible authentication-related localStorage items
    const itemsToRemove = [
      'token',
      'loggedIn',
      'userEmail',
      'userRole',
      'userPassword',
      'userData',
      'adminLoggedIn',
      'authToken',
      'sessionToken'
    ];

    itemsToRemove.forEach(item => {
      if (localStorage.getItem(item)) {
        console.log(`Removing localStorage item: ${item}`);
        localStorage.removeItem(item);
      }
    });

    // Clear sessionStorage as well
    sessionStorage.clear();

    // Reset all form states
    setAdminUsername('');
    setAdminPassword('');
    setAdminError('');
    setUserMode('signup');
    setUserName('');
    setUserEmail('');
    setUserPassword('');
    setUserShowPassword(false);
    setUserError('');
    setUserSuccess('');
    setUserLoginEmail('');
    setUserLoginPassword('');
    setUserLoginShowPassword(false);
    setUserLoginError('');

    // Force a page reload to clear any React state
    console.log('Redirecting to login page');
    window.location.href = '/login';
  };

  return (
    <div className={styles.container}>
      {/* Top Logo */}
      <img src={logoImage} alt="Clinic Logo" className={styles.mainLogo} />

      <div className={styles.cardsWrapper}>
        {/* Admin Login Card */}
        <div className={styles.card}>
          <h2 className={styles.heading}>Admin Login</h2>
          <div className={styles.subtext}>Sign in to continue.</div>
          {adminLoggedIn && (
            <button
              onClick={handleAdminLogout}
              className={styles.logoutButton}
            >
              Admin Logout
            </button>
          )}
          <form onSubmit={handleAdminLogin} style={{ width: '100%' }}>
            {adminError && <div className={styles.error}>{adminError}</div>}
            <input
              type="text"
              placeholder="Email"
              value={adminUsername}
              onChange={e => setAdminUsername(e.target.value)}
              required
              className={styles.input}
            />
            <div style={{ position: 'relative', marginBottom: 24, width: '100%' }}>
              <input
                type={adminShowPassword ? 'text' : 'password'}
                placeholder="Password"
                value={adminPassword}
                onChange={e => setAdminPassword(e.target.value)}
                required
                className={styles.passwordInput}
              />
              <span
                onClick={() => setAdminShowPassword((prev) => !prev)}
                className={styles.passwordToggle}
                aria-label={adminShowPassword ? 'Hide password' : 'Show password'}
                tabIndex={0}
                role="button"
              >
                {adminShowPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            <button type="submit" className={styles.button}>
              Login
            </button>
          </form>
        </div>
        {/* User Signup/Login Card */}
        <div className={styles.card}>
          {userMode === 'signup' ? (
            <>
              <h2 className={styles.heading}>Create Account</h2>
              <div className={styles.subtext}>
                Already Registered?{' '}
                <span
                  className={styles.link}
                  onClick={() => {
                    setUserMode('login');
                    setUserError('');
                    setUserSuccess('');
                  }}
                >
                  Log in here.
                </span>
              </div>
              <form onSubmit={handleUserSignup} style={{ width: '100%' }}>
                {userError && <div className={styles.error}>{userError}</div>}
                {userSuccess && <div className={styles.success}>{userSuccess}</div>}
                <input
                  type="text"
                  placeholder="Name"
                  value={userName}
                  onChange={e => setUserName(e.target.value)}
                  required
                  className={styles.input}
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={userEmail}
                  onChange={e => setUserEmail(e.target.value)}
                  required
                  className={styles.input}
                />
                <div style={{ position: 'relative', marginBottom: 24, width: '100%' }}>
                  <input
                    type={userShowPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={userPassword}
                    onChange={e => setUserPassword(e.target.value)}
                    required
                    className={styles.passwordInput}
                  />
                  <span
                    onClick={() => setUserShowPassword((prev) => !prev)}
                    className={styles.passwordToggle}
                    aria-label={userShowPassword ? 'Hide password' : 'Show password'}
                    tabIndex={0}
                    role="button"
                  >
                    {userShowPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                <button type="submit" className={styles.button}>
                  Sign up
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className={styles.heading}>User Login</h2>
              <div className={styles.subtext}>
                Don't have an account?{' '}
                <span
                  className={styles.link}
                  onClick={() => {
                    setUserMode('signup');
                    setUserLoginError('');
                  }}
                >
                  Sign up here.
                </span>
              </div>
              {localStorage.getItem('loggedIn') === 'true' && localStorage.getItem('userRole') === 'user' && (
                <button
                  onClick={handleAdminLogout}
                  className={styles.logoutButton}
                >
                  User Logout
                </button>
              )}
              <form onSubmit={handleUserLogin} style={{ width: '100%' }}>
                {userLoginError && <div className={styles.error}>{userLoginError}</div>}
                <input
                  type="email"
                  placeholder="Email"
                  value={userLoginEmail}
                  onChange={e => setUserLoginEmail(e.target.value)}
                  required
                  className={styles.input}
                />
                <div style={{ position: 'relative', marginBottom: 24, width: '100%' }}>
                  <input
                    type={userLoginShowPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={userLoginPassword}
                    onChange={e => setUserLoginPassword(e.target.value)}
                    required
                    className={styles.passwordInput}
                  />
                  <span
                    onClick={() => setUserLoginShowPassword((prev) => !prev)}
                    className={styles.passwordToggle}
                    aria-label={userLoginShowPassword ? 'Hide password' : 'Show password'}
                    tabIndex={0}
                    role="button"
                  >
                    {userLoginShowPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                <button type="submit" className={styles.button}>
                  Login
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Back to Home Button */}
      <button
        className={styles.backHomeBtn}
        onClick={() => navigate('/')}
      >
        <FaHome /> Back to Home
      </button>
    </div>
  );
};

export default Login; 
